import { useSelector, useDispatch } from 'react-redux';
import { UpgradeIncentiveHelper } from 'view/helpers';
import { openModal } from 'data/reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import { INCENTIVE_SCENARIOS } from 'utils/subscriptionPlanUtils';

/**
 * Custom hook for managing upgrade incentive logic
 * @returns {Object} Methods to show upgrade incentives and user info
 */
export const useUpgradeIncentive = () => {
	const dispatch = useDispatch();
	const { userId, isLoggedIn, currentSubscription, currentPlan } = useSelector(
		(state) => ({
			userId: state.user.userData?.id,
			isLoggedIn: !!(state.user.authToken || state.user.token || state.user.userData?.id),
			currentSubscription: state.subscription.currentSubscription,
			currentPlan: state.subscription.currentSubscription?.plan,
		})
	);
	// The API returns either a flat subscription or one nested under
	// `subscription`; only treat the user as free when neither carries an id.
	const isFreeUser =
		!(currentSubscription?.id || currentSubscription?.subscription?.id) ||
		!currentPlan ||
		currentPlan?.name?.toLowerCase().includes('free');

	//todo: refactor this weird logic, need an enterprise test user to test on prod
	const isEnterpriseUser =
		currentPlan?.name?.toLowerCase().includes('enterprise') ||
		currentPlan?.id === 'enterprise';

	/**
	 * Show upgrade incentive for free users hitting feature limits
	 * @param {Object} options - Additional options
	 */
	const showFeatureLimitIncentive = (options = {}) => {
		if (!isLoggedIn && !options.force) return false;

		// Only show for free users or users without subscription
		if (!isFreeUser && !options.force) return false;
		if (isEnterpriseUser && !options.force) return false;

		return UpgradeIncentiveHelper.showUpgradeIncentiveIfAllowed(
			dispatch,
			openModal,
			MODAL_TYPES,
			INCENTIVE_SCENARIOS.FREE_USER_FEATURE,
			userId,
			{
				scenario: INCENTIVE_SCENARIOS.FREE_USER_FEATURE,
				currentPlan,
				currentSubscription,
				...options,
			}
		);
	};

	const showUpgradePlanIncentive = (options = {}) => {
		if (isEnterpriseUser && !options.force) return false;

		return UpgradeIncentiveHelper.showUpgradeIncentiveIfAllowed(
			dispatch,
			openModal,
			MODAL_TYPES,
			INCENTIVE_SCENARIOS.FREE_USER_UPGRADE_PLAN,
			userId,
			{
				scenario: INCENTIVE_SCENARIOS.FREE_USER_UPGRADE_PLAN,
				currentPlan,
				currentSubscription,
				...options,
			}
		);
	};

	/**
	 * Show upgrade incentive for canceling users
	 * @param {Object} options - Additional options including onProceedWithCancel
	 */
	const showCancellationIncentive = (options = {}) => {
		if (!isLoggedIn || !userId) return false;

		// Only show for users with active subscriptions
		// if (!currentSubscription) return false;

		return UpgradeIncentiveHelper.showUpgradeIncentiveIfAllowed(
			dispatch,
			openModal,
			MODAL_TYPES,
			INCENTIVE_SCENARIOS.CANCEL_USER,
			userId,
			{
				scenario: INCENTIVE_SCENARIOS.CANCEL_USER,
				currentPlan,
				currentSubscription,
				...options,
			}
		);
	};

	/**
	 * Show daily upgrade incentive for free users
	 * @param {Object} options - Additional options
	 */
	const showDailyIncentive = (options = {}) => {
		if (!isLoggedIn || !userId) return false;

		// Only show for free users or users without subscription
		if (!isFreeUser) return false;
		if (isEnterpriseUser) return false;

		return UpgradeIncentiveHelper.showUpgradeIncentiveIfAllowed(
			dispatch,
			openModal,
			MODAL_TYPES,
			INCENTIVE_SCENARIOS.FREE_USER_DAILY,
			userId,
			{
				scenario: INCENTIVE_SCENARIOS.FREE_USER_DAILY,
				currentPlan,
				currentSubscription,
				...options,
			}
		);
	};

	/**
	 * Reset session tracking (call on app load)
	 */
	const resetSession = () => {
		if (userId) {
			UpgradeIncentiveHelper.resetSessionTracking(userId);
		}
	};

	/**
	 * Get analytics for current user
	 */
	const getAnalytics = () => {
		if (!userId) return null;
		return UpgradeIncentiveHelper.getUpgradeIncentiveAnalytics(userId);
	};

	return {
		showFeatureLimitIncentive,
		showCancellationIncentive,
		showDailyIncentive,
		showUpgradePlanIncentive,
		resetSession,
		getAnalytics,
		userId,
		isLoggedIn,
	};
};
